import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { ClickAwayListener } from '@mui/material';

import { MENTION_DROPDOWN_ATTR } from 'containers/Tiptap/components/tiptap-ui/comment-ui/comment-mention/suggestion';

import ToolbarContent from './ToolbarContent';
import { Toolbar, StyledToolbarContent } from './styles';

interface ToolbarPosition {
  x: number;
  y: number;
  visible: boolean;
}

const TiptapTextSelectionToolbar: React.FC<{
  editor: Editor | null;
}> = ({ editor }) => {
  const [position, setPosition] = useState<ToolbarPosition>({
    x: 0,
    y: 0,
    visible: false,
  });

  const toolbarRef = useRef<HTMLDivElement>(null);
  const isClickingToolbar = useRef(false);
  const selectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getSelectionPosition = useCallback((): { x: number; y: number } | null => {
    if (!editor || !editor.isFocused) {
      return null;
    }

    const { selection } = editor.state;
    if (selection.empty) {
      return null;
    }

    const { from, to } = selection;
    const coords = editor.view.coordsAtPos(from);
    const endCoords = editor.view.coordsAtPos(to);

    const x = (coords.left + endCoords.right) / 2;
    const y = coords.top + 40; // Position under the selection

    return { x, y };
  }, [editor]);

  const updateToolbarVisibility = useCallback(
    (forceHide = false): void => {
      if (!editor) return;
      const { selection } = editor.state;
      const textSelection = editor.state.doc.textBetween(selection.from, selection.to, ' ');

      if (forceHide || textSelection.length === 0 || selection.empty) {
        setPosition((prev) => ({ ...prev, visible: false }));
      } else {
        const pos = getSelectionPosition();
        if (pos) {
          setPosition({
            x: pos.x,
            y: pos.y,
            visible: true,
          });
        }
      }
    },
    [editor, getSelectionPosition]
  );

  const debouncedUpdateToolbar = useCallback((): void => {
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    selectionTimeoutRef.current = setTimeout(() => {
      updateToolbarVisibility();
    }, 300);
  }, [updateToolbarVisibility]);

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = (): void => {
      if (editor.state.selection.empty) {
        if (selectionTimeoutRef.current) {
          clearTimeout(selectionTimeoutRef.current);
        }
        updateToolbarVisibility(true);
        return;
      }
      debouncedUpdateToolbar();
    };

    // Listen to TipTap events
    editor.on('selectionUpdate', handleSelectionUpdate);
    editor.on('update', handleSelectionUpdate);

    // Listen to mouse events for better responsiveness
    const editorElement = editor.view.dom;
    const handleMouseUp = (): void => {
      // Small delay to ensure selection is updated
      window.setTimeout(handleSelectionUpdate, 10);
    };

    editorElement.addEventListener('mouseup', handleMouseUp);

    // Listen to document selection changes
    const handleDocumentSelection = (): void => {
      if (editor.isFocused) {
        handleSelectionUpdate();
      }
    };

    document.addEventListener('selectionchange', handleDocumentSelection);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      editor.off('update', handleSelectionUpdate);
      editorElement.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectionchange', handleDocumentSelection);

      // Clear timeout on cleanup
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [editor, debouncedUpdateToolbar, updateToolbarVisibility]);

  // Adjust position to keep toolbar in viewport
  useEffect(() => {
    if (position.visible && toolbarRef.current) {
      const toolbarRect = toolbarRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = position.x;
      let adjustedY = position.y;

      // Adjust horizontal position
      if (position.x + toolbarRect.width / 2 > viewportWidth) {
        adjustedX = viewportWidth - toolbarRect.width / 2 - 10;
      } else if (position.x - toolbarRect.width / 2 < 0) {
        adjustedX = toolbarRect.width / 2 + 10;
      }

      // Adjust vertical position
      if (position.y < 0) {
        // If toolbar would be above viewport, position it below the selection
        const pos = getSelectionPosition();
        if (pos) {
          adjustedY = pos.y + 30; // Position below the selection
        }
      } else if (position.y + toolbarRect.height > viewportHeight) {
        // If toolbar would be below viewport, position it above the selection
        const pos = getSelectionPosition();
        if (pos) {
          adjustedY = pos.y - toolbarRect.height - 10;
        }
      }

      if (adjustedX !== position.x || adjustedY !== position.y) {
        setPosition((prev) => ({
          ...prev,
          x: adjustedX,
          y: adjustedY,
        }));
      }
    }
  }, [position.visible, position.x, position.y, getSelectionPosition]);

  const handleClickAway = useCallback(
    (event: Event): void => {
      if (toolbarRef.current && toolbarRef.current.contains(event.target as Node)) {
        return;
      }
      if (editor && editor.view.dom.contains(event.target as Node)) {
        return;
      }
      if ((event.target as Element)?.closest?.(`[${MENTION_DROPDOWN_ATTR}]`)) {
        return;
      }
      updateToolbarVisibility(true);
    },
    [editor, updateToolbarVisibility]
  );

  const handleToolbarMouseDown = useCallback((): void => {
    isClickingToolbar.current = true;
  }, []);

  const handleToolbarMouseUp = useCallback((): void => {
    setTimeout(() => {
      isClickingToolbar.current = false;
    }, 100);
  }, []);

  if (!position.visible || !editor) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Toolbar
        position={position}
        role="toolbar"
        aria-label="Text formatting toolbar"
        ref={toolbarRef}
        onMouseDown={handleToolbarMouseDown}
        onMouseUp={handleToolbarMouseUp}
      >
        <StyledToolbarContent>
          <ToolbarContent editor={editor} onClose={() => updateToolbarVisibility(true)} />
        </StyledToolbarContent>
      </Toolbar>
    </ClickAwayListener>
  );
};

export default TiptapTextSelectionToolbar;
